import React, { useEffect, useState } from 'react';
import { findComponentElementIdByEvent } from '../../utils';
import { EpButton } from '../EpButton';
import { EpDropdown } from '../EpDropdown';

import './EpButtonGroup.scss';
import {
  IEpButtonGroupGeneral,
  IEpButtonGroupListData,
} from '../../interfaces/interfaces/components';
import { handlePropCustomFunc } from '../../utils/events';

interface EpButtonGroupProps {
  buttonGroupId: string;
  buttonGroupType:
    | 'primary'
    | 'secondary'
    | 'link-nav-secondary'
    | 'link-nav-primary';
  buttonGroupSize: 'sm' | 'md' | 'lg';
  buttonGroupIsFullWidth?: boolean;
  buttonGroupIsFullHeight?: boolean;
  buttonGroupListData: IEpButtonGroupListData[];
  customClasses?: string;
}

export const EpButtonGroup: React.FC<EpButtonGroupProps> = ({
  buttonGroupId,
  buttonGroupType,
  buttonGroupSize,
  buttonGroupIsFullWidth = false,
  buttonGroupIsFullHeight = false,
  buttonGroupListData,
  customClasses,
}) => {
  const [buttonGroupData, setButtonGroupData] = useState<
    IEpButtonGroupListData[]
  >(buttonGroupListData || []);
  const [currentClickedButtonId, setCurrentClickedButtonId] = useState<
    null | string
  >(null);
  const [currentDropdownActiveId, setCurrentDropdownActiveId] = useState<
    null | string
  >(null);

  const updateActiveButtonStatus = (
    button: IEpButtonGroupListData,
  ): IEpButtonGroupListData => {
    const dropdownMenuListData =
      (button.buttonDropdownMenuListData as IEpButtonGroupGeneral[]) || null;
    const activeButtonIsADropdownChild = dropdownMenuListData?.find(
      (button: IEpButtonGroupGeneral) =>
        button.buttonId === currentClickedButtonId,
    );

    if (
      button.buttonId === currentClickedButtonId ||
      button.dropdownId === currentDropdownActiveId
    ) {
      button.buttonIsActive = true;
    } else {
      button.buttonIsActive = false;
    }

    if (
      button.dropdownId &&
      button.buttonIsActive &&
      !activeButtonIsADropdownChild
    ) {
      button.buttonIsActive = false;
    }
    return button;
  };

  const handleActiveButton = (): void => {
    if (!buttonGroupData || !Array.isArray(buttonGroupData)) return;

    setButtonGroupData(
      buttonGroupData.map((button) => {
        if (button.buttonDropdownMenuListData) {
          button.buttonDropdownMenuListData =
            button.buttonDropdownMenuListData.map(
              (button: IEpButtonGroupGeneral) =>
                updateActiveButtonStatus(button),
            );
        }

        return updateActiveButtonStatus(button);
      }),
    );
  };

  const handleButtonGroupClick = (
    event: React.MouseEvent<HTMLDivElement>,
  ): void => {
    const target = event.target as HTMLElement;

    if (
      !target.closest('.ep-button') ||
      target.closest('.ep-dropdown__trigger-element')
    )
      return;

    const isDropdownItem: HTMLElement | null = target.closest('.ep-dropdown');

    if (isDropdownItem) {
      setCurrentDropdownActiveId(isDropdownItem.getAttribute('id'));
    }

    setCurrentClickedButtonId(findComponentElementIdByEvent(event));
  };

  useEffect(() => {
    if (!buttonGroupData || !Array.isArray(buttonGroupData)) return;

    const currentActiveButton = buttonGroupData.find(
      ({ buttonIsActive }) => buttonIsActive,
    );

    if (currentActiveButton?.buttonIsActive) {
      handlePropCustomFunc(currentActiveButton.buttonOnClickFunc);
      setCurrentClickedButtonId(currentActiveButton.buttonId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleActiveButton();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClickedButtonId, currentDropdownActiveId]);

  useEffect(() => {
    setButtonGroupData(buttonGroupListData || []);
  }, [buttonGroupListData]);

  return (
    <div
      id={buttonGroupId}
      className={`
      btn-group
      ep-button-group
      ${customClasses || ''}
      ${buttonGroupIsFullWidth ? 'ep-button-group--full-width' : ''}
      ${buttonGroupIsFullHeight ? 'ep-button-group--full-height' : ''}`}
      role="group"
      onClick={handleButtonGroupClick}
    >
      {buttonGroupData?.map((item: IEpButtonGroupListData) => {
        return item.buttonDropdownMenuListData ? (
          <EpDropdown
            key={item.buttonId}
            dropdownId={item.dropdownId}
            dropdownMenuPlacement={'left'}
            dropdownEvent={item.dropdownEvent}
            {...(item.buttonDropdownMenuListData && {
              dropdownDefaultListItems: item.buttonDropdownMenuListData,
            })}
            dropdownTriggerElement={
              <EpButton
                buttonId={item.buttonId}
                buttonType={
                  buttonGroupType !== 'link-nav-primary' &&
                  buttonGroupType !== 'link-nav-secondary'
                    ? `${buttonGroupType}-outline`
                    : buttonGroupType
                }
                buttonSize={buttonGroupSize}
                buttonText={item.buttonText}
                buttonIsDisabled={item.buttonIsDisabled}
                buttonRightIconName={'SolidCaretDown'}
                buttonOnClickFunc={(event: Event) =>
                  handlePropCustomFunc(item.buttonOnClickFunc, event)
                }
                buttonIsActive={item.buttonIsActive}
                buttonIsFullWidth={buttonGroupIsFullWidth}
                buttonIsFullHeight={buttonGroupIsFullHeight}
              />
            }
          />
        ) : (
          <EpButton
            key={item.buttonId}
            buttonId={item.buttonId}
            buttonType={
              buttonGroupType !== 'link-nav-primary' &&
              buttonGroupType !== 'link-nav-secondary'
                ? `${buttonGroupType}-outline`
                : buttonGroupType
            }
            buttonSize={buttonGroupSize}
            buttonText={item.buttonText}
            buttonIsDisabled={item.buttonIsDisabled}
            buttonLeftIconName={item.buttonLeftIconName}
            buttonOnClickFunc={(event: Event) =>
              handlePropCustomFunc(item.buttonOnClickFunc, event)
            }
            buttonIsActive={item.buttonIsActive}
            buttonIsFullWidth={buttonGroupIsFullWidth}
            buttonIsFullHeight={buttonGroupIsFullHeight}
          />
        );
      })}
    </div>
  );
};
